import React, { useCallback, FC, useMemo, useState } from 'react';
import { Button, Table } from 'src/common';
import { useTranslation, Trans } from 'react-i18next';
import styles from './index.module.scss';
import { ColumnProps } from 'antd/lib/table';
import { GetComponentProps } from 'rc-table/lib/interface';
import classNames from 'classnames';
import { TableRowSelection } from 'antd/lib/table/interface';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { useGetOrganisationsByIds } from 'src/models/organisations/hooks';
import { IApiOrganization } from 'src/models/organisations/types';
const VISIBLE_ITEMS_COUNT = 10;

function useTableColumns(
  onDeleteOrgs: () => void,
  selectedIds: string[],
  loading: boolean
): ColumnProps<IApiOrganization>[] {
  const { t } = useTranslation();
  return useMemo(() => {
    return [
      {
        title: t('Tenders.BuyersFilter.organizationNumber'),
        dataIndex: 'organisationNumber',
        key: 'organisationNumber',
        ellipsis: true,
        className: styles.colOrgNumber,
        sorter: (a, b) => a.organisationNumber.localeCompare(b.organisationNumber),
        sortDirections: ['descend', 'ascend'],
        render: (_, record) => <span>{record.organisationNumber}</span>
      },
      {
        title: t('Company.company'),
        dataIndex: 'organisationName',
        key: 'organisationName',
        ellipsis: true,
        className: styles.colName,
        sorter: (a, b) =>
          a.organisationName && b.organisationName ? a.organisationName.localeCompare(b.organisationName, 'sv') : -1,
        sortDirections: ['descend', 'ascend'],
        render: (_, record) => {
          return <span>{record.organisationName || t('Common.notFound')}</span>;
        }
      },
      {
        title: () => {
          const isDisabled = !selectedIds.length;
          return (
            <ul className={styles.actions}>
              <li className={styles.action}>
                <Button
                  type={'text'}
                  onClick={onDeleteOrgs}
                  disabled={isDisabled}
                  icon={<FontAwesomeIcon icon={faTrash} />}
                />
              </li>
            </ul>
          );
        },
        dataIndex: 'actions',
        key: 'actions',
        className: styles.colActions,
        onCell: () => ({
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();
          }
        }),
        render: (_, record) => {
          return (
            !record.organisationName &&
            !loading && (
              <ul className={styles.actions}>
                <li className={styles.action}>
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                </li>
              </ul>
            )
          );
        }
      }
    ];
  }, [loading, onDeleteOrgs, selectedIds.length, t]);
}

interface IProps {
  orgNumbers: string[];
  removeOrgs: (selectedIds: string[], updatingIds: string[], onComplete: () => void) => void;
}

export const AdminCompaniesList: FC<IProps> = props => {
  const { orgNumbers, removeOrgs } = props;
  const { t } = useTranslation();

  const { data: organizations, loading: organizationsLoading } = useGetOrganisationsByIds(orgNumbers);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const resetSelectedCompanies = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const [isVisible, setIsVisible] = useState(false);
  const toggleVisible = useCallback(() => {
    setIsVisible(!isVisible);
  }, [isVisible]);

  const foundCompanies = useMemo(() => {
    return organizations.filter(org => orgNumbers.includes(org.organisationNumber));
  }, [orgNumbers, organizations]);

  const notFoundCompanies = useMemo(() => {
    return orgNumbers
      .filter(companyId => !foundCompanies.map(_ => _.organisationNumber).includes(companyId))
      .map(companyId => {
        return { organisationNumber: companyId, organisationName: null };
      });
  }, [orgNumbers, foundCompanies]);

  const companiesData: IApiOrganization[] = useMemo(() => {
    return [...foundCompanies, ...notFoundCompanies];
  }, [foundCompanies, notFoundCompanies]);

  const companiesDataLength = useMemo(() => {
    return companiesData.length;
  }, [companiesData.length]);

  const isContentHidden = useMemo(() => {
    return companiesData.length > VISIBLE_ITEMS_COUNT;
  }, [companiesData.length]);

  const data = useMemo(() => {
    return !isContentHidden || isVisible ? companiesData : companiesData.slice(0, VISIBLE_ITEMS_COUNT);
  }, [companiesData, isContentHidden, isVisible]);

  const onDeleteOrgs = useCallback(() => {
    const unselectedIds = [
      ...new Set(
        companiesData
          .filter(company => !selectedIds.includes(company.organisationNumber))
          .map(company => company.organisationNumber)
      )
    ];
    removeOrgs(selectedIds, unselectedIds, resetSelectedCompanies);
  }, [companiesData, removeOrgs, resetSelectedCompanies, selectedIds]);

  const columns: ColumnProps<IApiOrganization>[] = useTableColumns(onDeleteOrgs, selectedIds, organizationsLoading);
  const onRow: GetComponentProps<IApiOrganization> = useCallback(
    record => {
      return {
        onClick: e => {
          e.stopPropagation();
        },
        className: classNames({ [styles.isNotFound]: !record.organisationName && !organizationsLoading })
      };
    },
    [organizationsLoading]
  );

  const onSelectChange = useCallback(selectedKeys => {
    setSelectedIds(selectedKeys);
  }, []);

  const rowSelectionConfig: TableRowSelection<IApiOrganization> = {
    onChange: onSelectChange,
    preserveSelectedRowKeys: false,
    selectedRowKeys: selectedIds
  };

  return (
    <div className={styles.companiesList}>
      {companiesData && companiesData?.length !== 0 && (
        <>
          {!!notFoundCompanies.length && !organizationsLoading && (
            <p className={styles.note}>
              <Trans i18nKey={'AdminPage.competitorsFilterWarning'} count={notFoundCompanies.length} />
            </p>
          )}
          <Table
            loading={organizationsLoading}
            columns={columns}
            dataSource={data}
            onRow={onRow}
            rowKey={'organisationNumber'}
            rowClassName={styles.row}
            className={styles.table}
            rowSelection={rowSelectionConfig}
          />
          {isContentHidden && (
            <button
              className={classNames(styles.expandAction, { [styles.isNotFound]: !!notFoundCompanies.length })}
              onClick={toggleVisible}
            >
              <span className={styles.expandActionText}>
                {t(isVisible ? 'Common.showLess' : 'Tenders.BuyersFilter.showMoreItemsCount', {
                  count: companiesDataLength - VISIBLE_ITEMS_COUNT
                })}
              </span>
              <FontAwesomeIcon
                icon={faCaretDown}
                className={classNames(styles.expandActionIcon, { [styles.isExpanded]: isVisible })}
              />
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default AdminCompaniesList;
